import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { signOut } from "firebase/auth";
import { collection, getDoc, getDocs, doc, query, where } from "firebase/firestore";
import { QRCodeSVG } from "qrcode.react";
import Attendance from "./Attendance";
import Notice from "./Notice";
import Document from "./Document";
import CompanyDocs from "./CompanyDocs";
import Inquiry from "./Inquiry";
import Payslip from "./Payslip";
import Payroll from "./Payroll";
import CompanyCalendar, { ThisMonthHolidays } from "./CompanyCalendar";
import ShiftTable from "./ShiftTable";
import UserManagement from "./UserManagement";
import Settings from "./Settings";
import BillingSettings from "./BillingSettings";
import { generateKioskToken } from "../utils/constants";
import { fetchIsPaidPlan } from "../utils/plan";

function Dashboard({ user, permissions, companyId, isAdmin }) {
  // Stripe の画面から戻ってきたとき（?billing=…）は、お支払いの画面を開いた状態で始める
  const [page, setPage] = useState(() => {
    try {
      return isAdmin && new URLSearchParams(window.location.search).has("billing") ? "billing" : "home";
    } catch {
      return "home";
    }
  });
  const [pendingCount, setPendingCount] = useState(0);
  const [unreadNotices, setUnreadNotices] = useState(0);
  // ログイン中の本人の表示用情報（氏名・社員ID・会社独自の社員番号）
  const [me, setMe] = useState({ name: "", memberNo: "", employeeNo: "" });
  // null = 判定中。判定できるまではロック表示を出さない。
  const [isPaid, setIsPaid] = useState(null);
  const handleLogout = () => signOut(auth);

  const can = (key) => isAdmin || permissions?.[key] === true;
  const canApproveDocument = can("approveDocument");

  // 有料プランかどうか（ライブラリーはこれで制限する）
  useEffect(() => {
    let alive = true;
    (async () => {
      const paid = await fetchIsPaidPlan(companyId);
      if (alive) setIsPaid(paid);
    })();
    return () => { alive = false; };
  }, [companyId]);

  // 従業員からの未処理申請の件数。画面を移動するたびに取り直す。
  useEffect(() => {
    if (!canApproveDocument) { setPendingCount(0); return; }
    let alive = true;
    (async () => {
      try {
        const q = companyId
          ? query(collection(db, "documents"), where("companyId", "==", companyId), where("status", "==", "pending"))
          : query(collection(db, "documents"), where("status", "==", "pending"));
        const snap = await getDocs(q);
        if (alive) setPendingCount(snap.size);
      } catch (e) {
        console.error("未処理申請の件数取得に失敗しました:", e);
      }
    })();
    return () => { alive = false; };
  }, [canApproveDocument, companyId, page]);

  // 未読のお知らせ件数。既読IDは users/{uid}.readNoticeIds に入っている。
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const q = companyId
          ? query(collection(db, "notices"), where("companyId", "==", companyId))
          : collection(db, "notices");
        const [snap, userSnap] = await Promise.all([
          getDocs(q),
          getDoc(doc(db, "users", user.uid)),
        ]);
        const ids = userSnap.exists() ? (userSnap.data().readNoticeIds || []) : [];
        const count = snap.docs.filter(d => !ids.includes(d.id)).length;
        if (alive) setUnreadNotices(count);
      } catch (e) {
        console.error("未読お知らせの取得に失敗しました:", e);
      }
    })();
    return () => { alive = false; };
  }, [companyId, user.uid, page]);

  // 自分の氏名・社員ID・社員番号。社員番号は給与情報にあり、本人は自分の分だけ読める。
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const uSnap = await getDoc(doc(db, "users", user.uid));
        const u = uSnap.exists() ? uSnap.data() : {};
        let employeeNo = "";
        try {
          const pSnap = await getDoc(doc(db, "payrollProfiles", user.uid));
          if (pSnap.exists()) employeeNo = pSnap.data().employeeNo || "";
        } catch { /* 給与情報が無い・読めない場合は空のまま */ }
        if (alive) setMe({ name: u.name || "", memberNo: u.memberNo || "", employeeNo });
      } catch (e) {
        console.error("ユーザー情報の取得に失敗しました:", e);
      }
    })();
    return () => { alive = false; };
  }, [user.uid]);

  const renderPage = () => {
    switch (page) {
      case "attendance": return <Attendance user={user} isAdmin={isAdmin} companyId={companyId} canViewAll={can("viewAllAttendance")} canManageDevices={can("manageDevices")} userName={me.name} />;
      case "notice":    return <Notice user={user} isAdmin={can("postNotice")} companyId={companyId} canApproveDocument={canApproveDocument} onNavigate={setPage} />;
      case "document":  return <Document user={user} isAdmin={canApproveDocument} companyId={companyId} />;
      case "companydocs": return <CompanyDocs user={user} isAdmin={can("manageCompanyDocs")} companyId={companyId}
                                    isPaid={isPaid} isCompanyAdmin={isAdmin} onNavigate={setPage} />;
      case "payslip":   return <Payslip user={user} isAdmin={can("distributePayslip")} companyId={companyId} />;
      case "payroll":   return <Payroll user={user} companyId={companyId} isAdmin={isAdmin}
                                    isPaid={isPaid} isCompanyAdmin={isAdmin} onNavigate={setPage} />;
      case "calendar":  return <CompanyCalendar companyId={companyId} user={user} canEdit={isAdmin} />;
      case "shift":     return <ShiftTable user={user} companyId={companyId} canEdit={can("viewAllAttendance")} />;
      case "users":    return <UserManagement companyId={companyId} isAdmin={isAdmin}
                                    canPayroll={can("distributePayslip") && isPaid === true} user={user} />;
      case "settings": return <Settings companyId={companyId} user={user} />;
      case "billing":  return <BillingSettings companyId={companyId} user={user} />;
      case "inquiry":  return <Inquiry user={user} companyId={companyId} />;
      default:         return <Home setPage={setPage} isAdmin={isAdmin} canManageUsers={can("manageUsers")} canPayroll={can("distributePayslip")} companyId={companyId} user={user} pendingCount={pendingCount} unreadNotices={unreadNotices} isPaid={isPaid} me={me} />;
    }
  };

  return (
    <div style={st.container}>
      <header style={st.header}>
        <h1 style={st.logo} onClick={() => setPage("home")}>miyabi Company</h1>
        <div style={st.userInfo}>
          <span style={st.email}>{user.email}</span>
          <button style={st.logoutBtn} onClick={handleLogout}>ログアウト</button>
        </div>
      </header>
      <main>
        {page !== "home" && (
          <div style={{ padding: "16px 32px 0" }}>
            <button onClick={() => setPage("home")} style={{ background: "none", border: "none", color: "#2980b9", cursor: "pointer", fontSize: "14px", padding: 0 }}>
              ← メニューに戻る
            </button>
          </div>
        )}
        {renderPage()}
      </main>
    </div>
  );
}

function Home({ setPage, isAdmin, canManageUsers, canPayroll = false, companyId, user, pendingCount = 0, unreadNotices = 0, isPaid = null, me = {} }) {
  const [kioskToken, setKioskToken] = useState(generateKioskToken);

  useEffect(() => {
    const timer = setInterval(() => setKioskToken(generateKioskToken()), 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  // カードの説明文：未読・未処理があるときは内訳を出す
  const noticeParts = [];
  if (unreadNotices > 0) noticeParts.push(`未読${unreadNotices}件`);
  if (pendingCount > 0)  noticeParts.push(`申請${pendingCount}件`);
  const noticeDesc = noticeParts.length > 0 ? noticeParts.join("・") : "会社からのお知らせ";

  const appUrl = "https://miyabicompany-manager.web.app";
  const kioskUrl = `${appUrl}?kiosk=${kioskToken}${companyId ? `&cid=${companyId}` : ""}`;

  const menus = [
    { key: "attendance", icon: "🕐", title: "出退勤", desc: "出勤・退勤の打刻" },
    { key: "notice",     icon: "📢", title: "お知らせ", desc: noticeDesc, badge: unreadNotices + pendingCount },
    { key: "document",   icon: "📄", title: "書類提出", desc: "申請・書類の送付", badge: pendingCount },
    { key: "calendar",   icon: "📅", title: "会社カレンダー", desc: "休日・営業日の確認" },
    { key: "shift",      icon: "🗓", title: "シフト表", desc: "勤務予定の確認・作成" },
    { key: "companydocs", icon: "📚", title: "ライブラリー",
      desc: isPaid === false ? "有料プランでご利用いただけます" : "就業規則・マニュアルの閲覧",
      locked: isPaid === false },
    { key: "payslip",    icon: "💰", title: "給与明細", desc: "給与明細の確認" },
    ...(canPayroll ? [{ key: "payroll", icon: "🧮", title: "給与計算",
      desc: isPaid === false ? "有料プランでご利用いただけます" : "勤怠から給与を自動計算",
      locked: isPaid === false }] : []),
    ...((isAdmin || canManageUsers) ? [{ key: "users",    icon: "👥", title: "社員管理", desc: "登録申請の承認・社員一覧" }] : []),
    ...(isAdmin ? [{ key: "settings", icon: "⚙️", title: "設定",     desc: "通知設定・PC登録" }] : []),
    ...(isAdmin ? [{ key: "billing",  icon: "💳", title: "お支払い", desc: "プラン・カード登録" }] : []),
    { key: "inquiry",  icon: "✉️", title: "お問い合わせ", desc: "運営への連絡・不具合の報告" },
  ];

  return (
    <div style={st.main}>
      <h2 style={st.welcome}>{me.name ? `ようこそ、${me.name} さん` : "ようこそ！"}</h2>
      {companyId && <ThisMonthHolidays companyId={companyId} />}
      {(me.memberNo || me.employeeNo) && (
        <div style={st.idRow}>
          {me.memberNo  && <span style={st.idChip}>社員ID：{me.memberNo}</span>}
          {me.employeeNo && <span style={st.idChip}>社員番号：{me.employeeNo}</span>}
        </div>
      )}
      <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", backgroundColor: isAdmin ? "#e8f4fd" : "#eafaf1", color: isAdmin ? "#2980b9" : "#27ae60", padding: "6px 16px", borderRadius: "20px", fontSize: "14px", fontWeight: "bold", marginBottom: "16px" }}>
        {isAdmin ? "👑 管理者としてログイン中" : "👤 従業員としてログイン中"}
      </div>
      {isAdmin && companyId && (
        <div style={{ marginBottom: "20px", fontSize: "13px", color: "#7f8c8d" }}>
          会社コード：<code style={{ backgroundColor: "#f0f4f8", padding: "3px 10px", borderRadius: "6px", color: "#2c3e50", fontWeight: "bold", fontSize: "14px" }}>{companyId}</code>
          <button onClick={() => { navigator.clipboard.writeText(companyId); alert("コピーしました"); }}
            style={{ marginLeft: "8px", padding: "3px 10px", fontSize: "12px", border: "1px solid #ddd", borderRadius: "6px", cursor: "pointer", backgroundColor: "#fff" }}>
            コピー
          </button>
          <span style={{ marginLeft: "8px", fontSize: "12px", color: "#aaa" }}>（従業員に共有してください）</span>
        </div>
      )}
      <div style={st.qrBox}>
        <QRCodeSVG value={kioskUrl} size={120} />
        <div style={st.qrText}>
          <div style={st.qrTitle}>📱 打刻はこちら</div>
          <div style={st.qrNote}>スマホでQRコードを読み取ってください</div>
          <div style={{ fontSize: "12px", color: "#e74c3c", marginTop: "4px" }}>※5分ごとに自動更新されます</div>
        </div>
      </div>
      <div style={st.grid}>
        {menus.map(m => (
          <div key={m.key} style={{ ...st.card, ...(m.badge > 0 ? st.cardAlert : {}), ...(m.locked ? st.cardLocked : {}) }} onClick={() => setPage(m.key)}>
            {m.badge > 0 && <span style={st.cardBadge}>{m.badge}</span>}
            {m.locked && <span style={st.cardLock}>🔒</span>}
            <div style={st.cardIcon}>{m.icon}</div>
            <h3>{m.title}</h3>
            <p style={st.cardDesc}>{m.desc}</p>
          </div>
        ))}
      </div>
      <div style={{ marginTop: "28px", textAlign: "center", display: "flex", justifyContent: "center", gap: "12px", flexWrap: "wrap" }}>
        <a href="/manual.pdf" target="_blank" rel="noopener noreferrer"
          style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "10px 24px", backgroundColor: "#fff", border: "1px solid #2980b9", borderRadius: "8px", color: "#2980b9", fontSize: "14px", textDecoration: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
          📖 操作マニュアルを見る（PDF）
        </a>
        {isAdmin && (
          <a href="/admin_manual.pdf" target="_blank" rel="noopener noreferrer"
            style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "10px 24px", backgroundColor: "#fff", border: "1px solid #e67e22", borderRadius: "8px", color: "#e67e22", fontSize: "14px", textDecoration: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
            👑 管理者マニュアルを見る（PDF）
          </a>
        )}
      </div>
    </div>
  );
}

const st = {
  container: { minHeight: "100vh", backgroundColor: "#f0f4f8" },
  header: { backgroundColor: "#2c3e50", color: "#fff", padding: "16px 32px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  logo: { margin: 0, fontSize: "22px", cursor: "pointer" },
  userInfo: { display: "flex", alignItems: "center", gap: "16px" },
  email: { fontSize: "14px", opacity: 0.8 },
  logoutBtn: { padding: "8px 16px", backgroundColor: "#e74c3c", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer" },
  main: { padding: "40px 32px" },
  welcome: { color: "#2c3e50", marginBottom: "10px" },
  idRow: { display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "16px" },
  idChip: { fontSize: "12px", color: "#2c3e50", backgroundColor: "#eef2f6", borderRadius: "6px", padding: "4px 10px", fontFamily: "monospace" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "20px" },
  card: { position: "relative", backgroundColor: "#fff", padding: "32px 24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", textAlign: "center", cursor: "pointer" },
  cardAlert: { border: "2px solid #e74c3c" },
  cardBadge: { position: "absolute", top: "12px", right: "12px", minWidth: "22px", padding: "2px 8px", borderRadius: "12px", backgroundColor: "#e74c3c", color: "#fff", fontSize: "13px", fontWeight: "bold", lineHeight: "18px" },
  cardLocked: { backgroundColor: "#f7f8fa", color: "#95a5a6", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" },
  cardLock: { position: "absolute", top: "12px", right: "12px", fontSize: "16px" },
  cardIcon: { fontSize: "48px", marginBottom: "12px" },
  cardDesc: { color: "#7f8c8d", fontSize: "14px" },
  qrBox: { display: "flex", alignItems: "center", gap: "24px", backgroundColor: "#fff", padding: "20px 24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "28px", maxWidth: "420px" },
  qrText: { display: "flex", flexDirection: "column", gap: "6px" },
  qrTitle: { fontSize: "16px", fontWeight: "bold", color: "#2c3e50" },
  qrNote: { fontSize: "13px", color: "#7f8c8d" },
};

export default Dashboard;
